import { useMemo } from 'react';
import { DiningFlowEntryPoint } from '@msc-b2c/interfaces/enums';
import {
  DiningRoomI,
  OBSI,
  UseDiningBootstrapSeedParams,
  UseDiningBootstrapSeedResult,
} from '@msc-b2c/interfaces/interfaces';
import {
  buildSlotsFromShopObs,
  shouldDisableReserveNowFromShopObs,
} from '../utils/diningAvailabilityPolicy';

/**
 * Builds stable dining bootstrap state from INT130/INT135 sources.
 *
 * Responsibilities:
 * - choose the correct source per entry point (PRODUCT_CARD/CART/ALREADY_PURCHASED),
 * - normalize raw source data into drawer-consumable seed state,
 * - compute signatures used to reset derived availability state when seed changes.
 */
export const useDiningBootstrapSeed = ({
  entryPoint,
  alreadyPurchasedObsCard,
  persistedMaximumSlotsBookable,
  shopObsData,
  paxEditBootstrapOverride,
  itineraryDays,
  seedDiningRoomCode,
}: UseDiningBootstrapSeedParams): UseDiningBootstrapSeedResult => {
  /**
   * Resolves the active bootstrap payload.
   *
   * CART / ALREADY_PURCHASED flows can start from card data and later switch to
   * INT135 (pax-edit override) when richer eligibility metadata is required.
   */
  const bootstrapSource = useMemo<OBSI | null>(() => {
    if (
      entryPoint === DiningFlowEntryPoint.CART ||
      entryPoint === DiningFlowEntryPoint.ALREADY_PURCHASED
    ) {
      if (!alreadyPurchasedObsCard) {
        return null;
      }

      if (entryPoint === DiningFlowEntryPoint.CART && paxEditBootstrapOverride) {
        return paxEditBootstrapOverride;
      }

      return {
        code: alreadyPurchasedObsCard.code,
        availabilityStatus: 'AVAILABLE',
        deliveryDate: alreadyPurchasedObsCard.deliveryDate,
        description: alreadyPurchasedObsCard.description,
        isSurprise: false,
        type: alreadyPurchasedObsCard.type?.code,
        maximumSlotsBookable:
          persistedMaximumSlotsBookable ??
          alreadyPurchasedObsCard.maximumSlotsBookable ??
          alreadyPurchasedObsCard.diningRoomsBookingLimit,
        diningRooms: alreadyPurchasedObsCard.diningRooms,
        prices: alreadyPurchasedObsCard.prices ?? {
          total: {},
          taxes: {},
          perPassenger: [],
        },
      } as OBSI;
    }

    return shopObsData ?? null;
  }, [
    alreadyPurchasedObsCard,
    entryPoint,
    paxEditBootstrapOverride,
    persistedMaximumSlotsBookable,
    shopObsData,
  ]);

  const normalizedBootstrapDiningRooms: DiningRoomI[] = Array.isArray(bootstrapSource?.diningRooms)
    ? (bootstrapSource.diningRooms as DiningRoomI[])
    : bootstrapSource?.diningRooms
    ? [bootstrapSource.diningRooms as DiningRoomI]
    : [];

  /**
   * Signature consumed by higher-level hooks/effects to know when baseline
   * source data has meaningfully changed.
   */
  const bootstrapSourceSignature = JSON.stringify({
    entryPoint,
    code: bootstrapSource?.code,
    availabilityStatus: bootstrapSource?.availabilityStatus,
    maximumSlotsBookable: bootstrapSource?.maximumSlotsBookable,
    diningRoomsBookingLimit: bootstrapSource?.diningRoomsBookingLimit,
    diningRooms: normalizedBootstrapDiningRooms.map((room) => ({
      code: room.code,
      description: room.description,
      options: room.options,
      timeSlots: room.timeSlots,
      maximumBookingLimit: room.maximumBookingLimit,
      allowedQuantity: room.allowedQuantity,
    })),
  });

  /**
   * Hydration signature also includes itinerary and room filters because those
   * affect rendered seed slots even when raw source payload is unchanged.
   */
  const bootstrapHydrationSignature = JSON.stringify({
    bootstrapSourceSignature,
    itineraryDays: itineraryDays.map((day) => day.date),
    seedDiningRoomCode,
    persistedMaximumSlotsBookable,
  });

  /**
   * Resolves booking limit with persisted cart value taking precedence.
   */
  const bootstrapMaximumSlotsBookable = useMemo(() => {
    const persisted =
      persistedMaximumSlotsBookable != null && persistedMaximumSlotsBookable > 0
        ? persistedMaximumSlotsBookable
        : undefined;

    if (!bootstrapSource) {
      return Math.max(1, persisted ?? 1);
    }

    const fromSource =
      bootstrapSource.maximumSlotsBookable ?? bootstrapSource.diningRoomsBookingLimit;

    const resolved =
      persisted ?? (fromSource != null && fromSource > 0 ? fromSource : undefined) ?? 1;

    return Math.max(1, resolved);
  }, [bootstrapSource, persistedMaximumSlotsBookable]);

  /**
   * Normalized dining room list used by date/time step selectors.
   */
  const bootstrapAvailableDiningRooms = useMemo<DiningRoomI[]>(() => {
    const raw = bootstrapSource?.diningRooms;
    if (!raw) {
      return [];
    }

    return Array.isArray(raw) ? raw : [raw as DiningRoomI];
  }, [bootstrapSource]);

  /**
   * Seed slots expanded per itinerary date before INT136 room/date lookups run.
   */
  const bootstrapThematicSlots = useMemo(() => {
    if (!bootstrapSource) {
      return [];
    }

    return buildSlotsFromShopObs({
      itineraryDays,
      seedDiningRoomCode,
      shopObsResponse: bootstrapSource,
    });
  }, [bootstrapSource, itineraryDays, seedDiningRoomCode]);

  /**
   * Baseline reserve-now disabled state from bootstrap payload only.
   */
  const bootstrapIsReserveNowDisabled = useMemo(() => {
    if (!bootstrapSource) {
      return true;
    }

    return shouldDisableReserveNowFromShopObs(bootstrapSource);
  }, [bootstrapSource]);

  return {
    bootstrapSource,
    bootstrapHydrationSignature,
    bootstrapMaximumSlotsBookable,
    bootstrapAvailableDiningRooms,
    bootstrapThematicSlots,
    bootstrapIsReserveNowDisabled,
  };
};
